import { UK_TIMEZONE } from '@see-power-pft-common-js/utils/product-mapper';
import { addDays, isSameDay, startOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { AvailabilityFormDrawerEnabledFields } from 'pages/Availability/common/FormDrawer/types';
import { boolean, date, string } from 'yup';

/**
 * Shared Yup field schemas used by both the site-level and asset-level
 * availability form validation schemas.
 */

export const startField = (
  enabledFields: AvailabilityFormDrawerEnabledFields,
) =>
  date()
    .nullable()
    .typeError('Please select the start date and time.')
    .when([], (schema: ReturnType<typeof date>) =>
      enabledFields.start
        ? schema
            .required('Please select the start date and time.')
            .min(new Date(), 'Start date/time must be in the future.')
        : schema.notRequired(),
    );

export const endField = () =>
  date()
    .nullable()
    .typeError('Please select the end date and time.')
    .when('isOpenEnded', (isOpenEnded, schema) =>
      isOpenEnded
        ? schema.notRequired()
        : schema
            .required('Please select the end date and time.')
            .test(
              'is-greater',
              'End date/time must be greater than the start date/time',
              function (
                this: { parent: { start?: Date | string | null } },
                value: Date | string | null | undefined,
              ) {
                const { start } = this.parent;
                if (!value || !start) return true; // Let required handle empty values
                return new Date(value) > new Date(start);
              },
            ),
    )
    .test(
      'same-day-or-midnight-next',
      'End date/time must be on the same day as start date when repeat daily is enabled.',
      function (value) {
        const { start, repeatDaily } = this.parent;
        if (!repeatDaily || !value || !start) return true;

        const startDate = toZonedTime(new Date(start), UK_TIMEZONE);
        const endDate = toZonedTime(new Date(value), UK_TIMEZONE);

        return (
          isSameDay(startDate, endDate) ||
          +endDate === +startOfDay(addDays(startDate, 1))
        );
      },
    );

export const startImmediatelyField = () =>
  boolean()
    .nullable()
    .typeError('Please select whether the reduction starts immediately.')
    .required('Please select whether the reduction starts immediately.');

export const isOpenEndedField = () =>
  boolean()
    .nullable()
    .typeError('Please select whether the reduction is open-ended.')
    .required('Please select whether the reduction is open-ended.');

export const availabilityReasonField = () =>
  string()
    .nullable()
    .typeError('Please select a reason for the reduction.')
    .required('Please select a reason for the reduction.');

export const notesField = () => string().nullable().notRequired();

export const repeatEndInclusiveField = () =>
  date()
    .nullable()
    .typeError('Please select the end date for the repeat daily option.')
    .when('repeatDaily', (repeatDaily, schema) =>
      repeatDaily
        ? schema
            .required('Please select the end date for the repeat daily option.')
            .test(
              'is-greater',
              'Repeat end date must be greater than the start date',
              function (
                this: { parent: { start?: Date | string | null } },
                value: Date | string | null | undefined,
              ) {
                const { start } = this.parent;
                if (!value || !start) return true; // Let required handle empty values
                const startDate = new Date(start);
                const endDate = new Date(value);
                return (
                  endDate > startDate &&
                  startDate.toDateString() !== endDate.toDateString()
                );
              },
            )
        : schema.notRequired(),
    );
